import {React, useState, useEffect} from 'react';
import '../../App.css'
import { Grid, Box, Button, Paper, Stack, FormControl, InputLabel, Select, MenuItem, FormHelperText, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Description } from '@mui/icons-material';
import * as monthList from '../../services/monthList';
import * as yearList from '../../services/yearList';
import axios from 'axios';
import {useNavigate } from 'react-router-dom';
import Notification from '../Notification';
import * as XLSX from 'xlsx-js-style';
import { getShortCompanyName } from '../../services/companyService';

const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red' , marginLeft: '3px'}}>*</span>
  </span>
);

const monthOptions = monthList.getMonthList();
const yearOptions = yearList.getYearList();

const today = new Date();
const currentYear = today.getFullYear().toString();
const currentMonthIndex = today.getMonth();
const currentMonthName = monthOptions[currentMonthIndex].title;

const initialFValues={
  id: 0,
  month: currentMonthName,
  year: currentYear,
}

function ExcelFileGeneration({ isEmbedded = false }){
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [empDetails, setEmpDetails] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [notify, setNotify] = useState({isOpen: false, message: '', type: ''});
  const navigate = useNavigate();

  // Fetch employee data
  const fetchEmployeeData = async (month, year) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/report/get-month-wise-employees/${month}/${year}`
      );
      
      let employeeData = [];
      let company = '';
      
      // Handle new response structure: { companyName, data: [[...]] }
      if (response.data && response.data.companyName) {
        company = response.data.companyName;
        if (response.data.data && Array.isArray(response.data.data)) {
          employeeData = response.data.data.flat();
        }
      } else if (Array.isArray(response.data)) {
        // Fallback for old response structure
        if (response.data.length > 0 && Array.isArray(response.data[0])) {
          employeeData = response.data.flat();
        } else {
          employeeData = response.data;
        }
      }
      
      // Sort by net_pay in ascending order
      employeeData.sort((a, b) => {
        const netPayA = a.net_pay || 0;
        const netPayB = b.net_pay || 0;
        return netPayA - netPayB;
      });
      
      setCompanyName(company);
      setEmpDetails(employeeData);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({
          isOpen: true,
          message: 'Failed to fetch employee data',
          type: 'error'
        });
      }
    }
  };

  // Call API when component mounts
  useEffect(() => {
    fetchEmployeeData(values.month, values.year);
  }, []);

  // Call API when month or year changes
  useEffect(() => {
    if (values.month && values.year) {
      fetchEmployeeData(values.month, values.year);
    }
  }, [values.month, values.year]);

  const validate = () => {
    let temp = {}
    temp.month = values.month.length != 0 ? "" : "This field is required."
    temp.year = values.year.length != 0 ? "" : "This field is required."

    setErrors({
      ...temp
    })

    return Object.values(temp).every(x => x == "")
  }

  function handleInputChange(e){
    const {name, value} = e.target;
    setValues({
      ...values,
      [name]: value
    })
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  }

  // Helper function to clean account numbers
  const cleanDisplayValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    let cleaned = String(value);
    cleaned = cleaned.replace(/'/g, '');
    cleaned = cleaned.replace(/[''""]/g, '');
    cleaned = cleaned.trim();
    return cleaned;
  };

  // Generate and download Excel file
  const handleGenerateExcel = () => {
    if (!validate()) return;
    
    if (empDetails.length === 0) {
      setNotify({
        isOpen: true,
        message: 'No employee data available to generate Excel file!',
        type: 'error'
      });
      return;
    }

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Build worksheet data with company name header
    const wsData = [];
    
    // Row 1: Company Name (will be merged across all columns)
    wsData.push([companyName || 'Company', '', '', '', '']);
    
    // Row 2: Column Headers
    wsData.push(['Sr No', 'Employee Code', 'Employee Name', 'Account No', 'Net Pay']);
    
    // Data rows
    empDetails.forEach((emp, index) => {
      wsData.push([
        index + 1,
        emp.employee_code || '',
        emp.name || '',
        cleanDisplayValue(emp.ICICI_account_no || emp.account_number || ''),
        `₹ ${Math.round(emp.net_pay || 0).toLocaleString('en-IN')}`
      ]);
    });

    // Create worksheet from array
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Calculate column widths based on content
    const calculateColumnWidths = (data) => {
      const colWidths = [];
      const numCols = data[0]?.length || 5;
      
      // Get the max width for each column
      for (let col = 0; col < numCols; col++) {
        let maxWidth = 0;
        
        data.forEach((row, rowIndex) => {
          // Skip row 0 (company name - merged)
          if (rowIndex < 1) return;
          
          if (row[col] !== undefined && row[col] !== null) {
            let cellValue = String(row[col]);
            
            // Special handling for Net Pay column (index 4) - account for formatting with commas
            if (col === 4 && rowIndex >= 2) { // Data rows start at index 2
              cellValue = String(row[col]);
            }
            
            const cellWidth = cellValue.length;
            maxWidth = Math.max(maxWidth, cellWidth);
          }
        });
        
        // Add a small padding (1 character) for better readability
        colWidths.push({ wch: maxWidth + 1 });
      }
      
      return colWidths;
    };

    // Set column widths dynamically based on content
    ws['!cols'] = calculateColumnWidths(wsData);

    // Merge cells for company name header (A1:E1)
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }
    ];

    // Common border style
    const borderStyle = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    };

    // Style for company name header
    const companyNameStyle = {
      font: { bold: true, sz: 16, color: { rgb: "000000" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle
    };

    // Style for column headers
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4472C4" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle
    };

    // Style for data cells
    const dataStyle = {
      border: borderStyle,
      alignment: { vertical: "center" }
    };

    // Apply style to company name cell (A1) - merged cell
    if (ws['A1']) {
      ws['A1'].s = companyNameStyle;
    }
    
    // Remove empty cells in merged range (to prevent visible lines)
    for (let col = 1; col < 5; col++) {
      const emptyCellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (ws[emptyCellAddress]) {
        delete ws[emptyCellAddress];
      }
    }

    // Apply styles to column header cells (Row 2)
    const headerCells = ['A2', 'B2', 'C2', 'D2', 'E2'];
    headerCells.forEach(cell => {
      if (ws[cell]) {
        ws[cell].s = headerStyle;
      }
    });

    // Apply styles to data cells (starting from row 3)
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let row = 2; row <= range.e.r; row++) {
      for (let col = 0; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            ...dataStyle,
            alignment: {
              ...dataStyle.alignment,
              horizontal: col === 4 ? "right" : (col === 0 ? "center" : "left")
            }
          };
          // Format Net Pay as number with Indian formatting
          if (col === 4) {
            ws[cellAddress].z = '#,##0.00';
          }
        }
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Employee Salary');

    // Generate filename
    const filename = `${getShortCompanyName(companyName) || 'Report'} Report for Salary ${values.month.substring(0, 3)} ${values.year}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);

    setNotify({
      isOpen: true,
      message: 'Excel file generated successfully!',
      type: 'success'
    });
  };

  return(
    <Box sx={{ display: "flex", justifyContent: "center", margin: isEmbedded ? "0" : "10px", padding: isEmbedded ? "10px 20px" : "10px", fontWeight: "500px" }}>
      <Paper elevation={isEmbedded ? 0 : 5} sx={{ width: '100%', maxWidth: '1400px' }}>
        {!isEmbedded && (
          <Grid className="form-title" container spacing={2}>
            <Grid item>
              <Typography variant="h4" sx={{ textAlign: "center", margin: 5, display: "flex", alignItems: "center", gap: 1, marginBottom: "8px", marginTop: "4px" }}>
                <Description sx={{ width: 40, height: 40 }} />
                Excel File Generation
              </Typography>
            </Grid>
          </Grid>
        )}

        <Box sx={{ margin: isEmbedded ? "15px 30px" : "20px", marginTop: '0px' }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 2, mx: "10px" }}>
              <FormControl variant="outlined" sx={{ minWidth: 200 }} {...(errors.month && { error: true })}>
                <InputLabel><RequiredLabel label="Month" /></InputLabel>
                <Select label="Month" name="month" size="small" value={values.month} onChange={handleInputChange}>
                  <MenuItem value="">None</MenuItem>
                  {monthOptions.map(item => (<MenuItem key={item.id} value={item.title}>{item.title}</MenuItem>))}
                </Select>
                {errors.month && <FormHelperText>{errors.month}</FormHelperText>}
              </FormControl>
              <FormControl variant="outlined" sx={{ minWidth: 200 }} {...(errors.year && { error: true })}>
                <InputLabel><RequiredLabel label="Year" /></InputLabel>
                <Select label="Year" name="year" size="small" value={values.year} onChange={handleInputChange}>
                  <MenuItem value="">None</MenuItem>
                  {yearOptions.map(item => (<MenuItem key={item.id} value={item.title}>{item.title}</MenuItem>))}
                </Select>
                {errors.year && <FormHelperText>{errors.year}</FormHelperText>}
              </FormControl>
            </Box>
          </Stack>
        </Box>

        {/* Employee Data Table - same design as Income Tax */}
        {empDetails.length > 0 && (
          <Box sx={{ margin: "20px", marginTop: '0px' }}>
            <Stack spacing={2}>
              <Box sx={{ width: '100%', margin: "10px", background: '#ffffff', borderRadius: 2, overflow: 'hidden', border: '1px solid #e9ecef', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#495057', px: 2, pt: 1 }}>
                  Employee Records ({empDetails.length} employees)
                </Typography>
                <div style={{ height: '600px', width: '100%', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: '#fff', margin: '10px' }}>
                  <Table size="small" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <TableHead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#212529', color: 'white', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.2)' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px', width: '60px' }}>Sr No</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>Employee Code</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>Employee Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>Account No</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px', textAlign: 'right' }}>Net Pay</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {empDetails.map((employee, index) => (
                        <TableRow key={employee.employee_id || index} hover sx={{ backgroundColor: 'white' }}>
                          <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>{index + 1}</TableCell>
                          <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>{employee.employee_code}</TableCell>
                          <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>{employee.name}</TableCell>
                          <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>{cleanDisplayValue(employee.ICICI_account_no || employee.account_number || '')}</TableCell>
                          <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px', textAlign: 'right' }}>₹{Math.round(employee.net_pay || 0).toLocaleString('en-IN')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Box sx={{ display: "flex", justifyContent: "center", marginTop: "15px", pb: 2 }}>
                  <Button color="primary" variant="contained" onClick={handleGenerateExcel} disabled={empDetails.length === 0} sx={{ minWidth: '200px' }}>
                    Generate Excel File
                  </Button>
                </Box>
              </Box>
            </Stack>
          </Box>
        )}

      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  )
}

export default ExcelFileGeneration;
